import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { FileText, Upload, CheckCircle, Sparkles } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { v4 as uuidv4 } from 'uuid';
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import { Job } from "@/lib/schema";
import { createReferral, checkDuplicateReferral } from "@/api/referrals";
import { uploadReferralResume } from "@/api/storage";
import { normalizeEmail } from "@/lib/schema";
import { trackEvent } from "@/lib/firebase";

const referralFormSchema = z.object({
  jobId: z.string(),
  candidateFirstName: z.string().min(1, "First name is required"),
  candidateLastName: z.string().min(1, "Last name is required"),
  candidateEmail: z.string().email("Valid email is required"),
  candidateLinkedin: z.string().optional(),
  candidatePhone: z.string().optional(),
  referralNote: z.string().optional(),
  relationship: z.string().optional(),
});

type ReferralFormData = z.infer<typeof referralFormSchema>;

interface DuplicateAlert {
  show: boolean;
  title: string;
  description: string;
}

interface ReferralFormProps {
  job: Job;
  onClose: () => void;
}

export function ReferralForm({ job, onClose }: ReferralFormProps) {
  // Resume upload state
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeUrl, setResumeUrl] = useState("");
  const [resumeFileName, setResumeFileName] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const scrollPositionRef = useRef<number | null>(null);
  const isSelectingRef = useRef<boolean>(false);
  
  // Form state
  const [gdprConsent, setGdprConsent] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [duplicateAlert, setDuplicateAlert] = useState<DuplicateAlert>({ 
    show: false, 
    title: "", 
    description: "" 
  });

  // Hooks
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const formId = "referral-form";
  const { register, handleSubmit, setValue, reset, watch, formState: { errors } } = useForm<ReferralFormData>({
    resolver: zodResolver(referralFormSchema),
  });

  useEffect(() => {
    register("jobId");
    register("relationship"); // Register the relationship field
  }, [register]);

  useEffect(() => {
    setValue("jobId", job.id);
  }, [job.id, setValue]);

  // Get scroll container dynamically (no caching to avoid stale references)
  const getScrollContainer = useCallback(() => {
    // Updated to match the new scroll container in ReferralModal
    return document.querySelector('.main-content .overflow-y-auto.scrollbar-hide') || 
           document.querySelector('.overflow-y-auto.scrollbar-hide');
  }, []);

  // Lock scroll position during Select interactions
  useEffect(() => {
    let retryTimeoutId: NodeJS.Timeout | null = null;
    let attachedContainer: Element | null = null;
    
    const lockScroll = () => {
      const container = getScrollContainer();
      // Only lock if selecting AND we have a valid saved position
      if (container && isSelectingRef.current && scrollPositionRef.current !== null) {
        container.scrollTop = scrollPositionRef.current;
      }
    };

    const attachListener = (container: Element) => {
      container.addEventListener('scroll', lockScroll, { passive: true });
      attachedContainer = container;
    };

    const detachListener = () => {
      if (attachedContainer) {
        attachedContainer.removeEventListener('scroll', lockScroll);
        attachedContainer = null;
      }
    };

    // Try to get container
    const container = getScrollContainer();
    if (!container) {
      // Retry after a short delay if container not found yet
      retryTimeoutId = setTimeout(() => {
        const retryContainer = getScrollContainer();
        if (retryContainer) {
          attachListener(retryContainer);
        }
      }, 100);
    } else {
      attachListener(container);
    }
    
    return () => {
      // Clear retry timeout if still pending
      if (retryTimeoutId !== null) {
        clearTimeout(retryTimeoutId);
      }
      // Remove event listener if attached
      detachListener();
    };
  }, [getScrollContainer]);

  // Prevent scroll when GDPR consent changes
  useEffect(() => {
    const preventScrollIntoView = (e: Event) => {
      e.preventDefault();
      return false;
    };

    const checkboxElements = document.querySelectorAll('#gdprConsent, [id="gdprConsent"]');
    checkboxElements.forEach(element => {
      element.addEventListener('scrollIntoView', preventScrollIntoView);
    });

    return () => {
      checkboxElements.forEach(element => {
        element.removeEventListener('scrollIntoView', preventScrollIntoView);
      });
    };
  }, [gdprConsent]);

  const watchedLinkedin = watch("candidateLinkedin");
  const watchedRelationship = watch("relationship");

  // Helper function to upload with minimum 3-second delay for better UX
  const uploadWithMinDelay = async (file: File, email: string, jobId: string, candidateUuid: string) => {
    const startTime = Date.now();
    const minDelay = 3000; // 3 seconds minimum
    
    // Start upload immediately
    const uploadPromise = uploadReferralResume(file, email, candidateUuid, jobId);
    
    // Wait for both upload completion and minimum delay
    const [uploadResult] = await Promise.all([
      uploadPromise,
      new Promise(resolve => {
        const elapsed = Date.now() - startTime;
        const remaining = Math.max(0, minDelay - elapsed);
        setTimeout(resolve, remaining);
      })
    ]);
    
    return uploadResult;
  };

  const mutation = useMutation({
    mutationFn: async (data: ReferralFormData) => {
      if (!currentUser?.uid) {
        throw new Error("User not authenticated");
      }

      const normalizedEmail = normalizeEmail(data.candidateEmail);

      // Check for duplicate referral first
      const duplicate = await checkDuplicateReferral(job.id, normalizedEmail);
      if (duplicate) {
        throw new Error("This candidate has already been referred to this position");
      }

      let uploadedResumeUrl = resumeUrl;
      let uploadedResumeFileName = resumeFileName;
      // We need to create a uuid to reference the candidate if he doesnt exist
      const candidateUuid = uuidv4();
      

      // Upload resume file if one was selected with minimum 3-second delay
      if (resumeFile && !resumeUrl) {
        try {
          setIsUploading(true);
          const uploadResult = await uploadWithMinDelay(resumeFile, normalizedEmail, job.id, candidateUuid);
          uploadedResumeUrl = uploadResult.url;
          uploadedResumeFileName = uploadResult.fileName;
          setResumeUrl(uploadResult.url);
          setResumeFileName(uploadResult.fileName);
        } catch (error) {
          throw new Error("Failed to upload resume. Please try again.");
        } finally {
          setIsUploading(false);
        }
      }

      // Prepare candidate data according to insertCandidateSchema
      const candidateData = {
        id: candidateUuid,
        email: normalizedEmail,
        firstName: data.candidateFirstName,
        lastName: data.candidateLastName,
        linkedinUrl: data.candidateLinkedin,
        phoneNumber: data.candidatePhone,
        resumeUrl: uploadedResumeUrl,
        resumeFileName: uploadedResumeFileName || resumeFile?.name || "resume.pdf",
        firstReferredBy: currentUser.uid, // Required field for new candidates
      };

      // Create referral using Firebase callable function
      return await createReferral({
        candidateUuid: candidateUuid,
        candidateEmail: normalizedEmail,
        jobId: job.id,
        referralNote: data.referralNote,
        relationship: data.relationship,
        candidateLinkedin: data.candidateLinkedin,
        candidatePhone: data.candidatePhone,
        candidateData
      });
    },
    onSuccess: (referralId) => {
      queryClient.invalidateQueries({ queryKey: ["referrals"] });
      queryClient.invalidateQueries({ queryKey: ["user-stats"] });
      
      // Track referral submission
      trackEvent('submit_referral', {
        job_id: job.id,
        job_title: job.title,
        has_resume: !!resumeFile,
        user_id: currentUser?.uid,
      });
      
      // Check if this was a special case where we couldn't get the actual referral ID
      if (referralId === 'referral-created-check-admin') {
        // Show a special success message for this case
        toast({
          title: "Referral Submitted Successfully!",
          description: "Your referral has been submitted, but there was a technical issue with the response format. Please refresh the page to see your referral in the list.",
          variant: "default",
        });
      }
      
      // Show success state - let user close it themselves
      setShowSuccess(true);
    },
    onError: (error: any) => {
      let errorMessage = "An unexpected error occurred while submitting your referral.";
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.code) {
        // Handle Firebase function error codes
        switch (error.code) {
          case 'functions/already-exists':
          case 'already-exists':
            errorMessage = "This candidate has already been referred to this position.";
            break;
          case 'functions/failed-precondition':
          case 'failed-precondition':
            errorMessage = "This job is no longer available for referrals.";
            break;
          case 'functions/permission-denied':
          case 'permission-denied':
            errorMessage = "You don't have permission to create referrals.";
            break;
          case 'functions/unauthenticated':
          case 'unauthenticated':
            errorMessage = "Please sign in to submit referrals.";
            break;
          case 'functions/invalid-argument':
          case 'invalid-argument':
            errorMessage = "Invalid referral data. Please check all required fields.";
            break;
          case 'functions/internal':
          case 'internal':
            errorMessage = "A server error occurred. Please try again.";
            break;
          case 'functions/unavailable':
          case 'unavailable':
            errorMessage = "Service temporarily unavailable. Please try again later.";
            break;
          case 'functions/deadline-exceeded':
          case 'deadline-exceeded':
            errorMessage = "Request timed out. Please try again.";
            break;
          case 'functions/resource-exhausted':
          case 'resource-exhausted':
            errorMessage = "Service temporarily overloaded. Please try again later.";
            break;
          default:
            errorMessage = error.message || errorMessage;
        }
      }

      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Failed to submit referral",
        description: errorMessage,
        variant: "destructive"
      });
    },
  });

  const handleFileSelect = useCallback((file: File) => {
    setResumeFile(file);
    setResumeFileName(""); // Clear previous filename - will be set after upload
    // Reset the input value to allow re-selection (iOS Safari fix)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleRemoveResume = useCallback(() => {
    setResumeFile(null);
    setResumeUrl("");
    setResumeFileName("");
    // Reset the input value (iOS Safari fix)
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const triggerFileInput = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleGdprChange = useCallback((checked: boolean) => {
    // Multiple strategies to find and preserve scroll position
    const findScrollContainer = () => {
      // Try multiple selectors for different layouts
      const selectors = [
        '.overflow-y-auto.scrollbar-hide', // Mobile modal container
        'form[id*="desktop"].overflow-y-auto', // Desktop form container
        '.overflow-y-auto', // Generic fallback
        '[data-radix-dialog-content]', // Dialog content
        '.max-h-\\[90vh\\]' // Modal with max height
      ];
      
      for (const selector of selectors) {
        const element = document.querySelector(selector);
        if (element && element.scrollHeight > element.clientHeight) {
          return element;
        }
      }
      return null;
    };

    const scrollContainer = findScrollContainer();
    const currentScrollTop = scrollContainer?.scrollTop || 0;
    
    // Prevent any focus-related scrolling by temporarily disabling scroll behavior
    const originalScrollBehavior = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'auto';
    
    setGdprConsent(checked);
    
    // Restore scroll position and behavior
    requestAnimationFrame(() => {
      if (scrollContainer) {
        scrollContainer.scrollTop = currentScrollTop;
      }
      document.documentElement.style.scrollBehavior = originalScrollBehavior;
    });
  }, []);

  const handleSuccessClose = useCallback(() => {
    reset();
    setResumeFile(null);
    setResumeUrl("");
    setResumeFileName("");
    setShowSuccess(false);
    onClose();
  }, [reset, onClose]);

  const onSubmit = async (data: ReferralFormData) => {
    if (!job) return;
    
    // Validate that either LinkedIn profile or resume is provided
    if (!data.candidateLinkedin && !resumeFile && !resumeUrl) {
      // TODO: Integrate with Sentry for error tracking and reporting
      toast({
        title: "Required information missing",
        description: "Please provide either their LinkedIn profile or upload their CV to proceed.",
        variant: "destructive"
      });
      return;
    }
    
    // Check for duplicate referrals before proceeding
    try {
      const normalizedEmail = normalizeEmail(data.candidateEmail);
      const existingReferral = await checkDuplicateReferral(job.id, normalizedEmail);
      
      if (existingReferral) {
        const isOwnReferral = existingReferral.referrerId === currentUser?.uid;
        
        // Remove focus from current element before showing alert
        if (document.activeElement && document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        
        setDuplicateAlert({
          show: true,
          title: "Candidate Already Referred",
          description: isOwnReferral 
            ? "You have already referred this candidate for this position." 
            : "This candidate has already been referred for this position by someone else."
        });
        return;
      }
    } catch (error: any) {
      console.error('Error checking duplicate referral:', error);
      
      // Remove focus from current element before showing alert
      if (document.activeElement && document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      
      // More specific error handling
      let errorMessage = "Unable to verify referral. Please try again.";
      
      if (error.code === 'permission-denied') {
        errorMessage = "You don't have permission to check referrals. Please contact support.";
      } else if (error.code === 'unavailable') {
        errorMessage = "Service temporarily unavailable. Please try again in a moment.";
      } else if (error.message && error.message.includes('network')) {
        errorMessage = "Network error. Please check your connection and try again.";
      }
      
      setDuplicateAlert({
        show: true,
        title: "Validation Error",
        description: errorMessage
      });
      return;
    }
    
    mutation.mutate({
      ...data,
      jobId: job.id,
    });
  };

  // Success message component
  const SuccessMessage = () => (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center shadow-2xl">
        <div className="relative mb-6">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto relative">
            <CheckCircle className="w-12 h-12 text-green-600" />
            <div className="absolute -top-2 -right-2">
              <Sparkles className="w-8 h-8 text-yellow-500 animate-pulse" />
            </div>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-slate-800 mb-4">
          Referral Submitted Successfully! 🎉
        </h3>
        <p className="text-slate-600 mb-4">
          Your referral for <span className="font-semibold text-orange-600">{job.title}</span> has been sent to the recruiter for review.
        </p>
        <p className="text-sm text-slate-500">
          You'll be notified about the progress and earn AiR Points when they get hired!
        </p>
        <div className="mt-8">
          <Button 
            onClick={handleSuccessClose}
            className="w-full h-12 bg-gradient-to-b from-orange-400 to-orange-600 hover:from-orange-500 hover:to-orange-700 text-white font-bold rounded-lg shadow-lg hover:shadow-xl transition-all transform hover:scale-105 tracking-wide"
          >
            Continue
          </Button>
        </div>
      </div>
    </div>
  );

  // Memoize form content to prevent re-creation on every render (critical for iOS Safari keyboard)
  const FormContent = useMemo((): JSX.Element => (
    <>
      {/* Friend Information Section */}
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-slate-800">Your Referral's Details</h3>
          <p className="text-slate-600">Tell us about the person you're referring</p>
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="candidateFirstName" className="text-slate-700 font-medium">First Name *</Label>
            <Input
              id="candidateFirstName"
              {...register("candidateFirstName")}
              required
              className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
              autoCapitalize="words"
            />
            {errors.candidateFirstName && (
              <p className="text-sm text-red-600 mt-1">{errors.candidateFirstName.message}</p>
            )}
          </div>
          <div>
            <Label htmlFor="candidateLastName" className="text-slate-700 font-medium">Last Name *</Label>
            <Input
              id="candidateLastName"
              {...register("candidateLastName")}
              required
              className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
              autoCapitalize="words"
            />
            {errors.candidateLastName && (
              <p className="text-sm text-red-600 mt-1">{errors.candidateLastName.message}</p>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="candidateEmail" className="text-slate-700 font-medium">Email Address *</Label>
          <Input
            id="candidateEmail"
            type="email"
            {...register("candidateEmail")}
            required
            className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
            placeholder="their.email@example.com"
            autoCapitalize="none"
            autoCorrect="off"
          />
          {errors.candidateEmail && (
            <p className="text-sm text-red-600 mt-1">{errors.candidateEmail.message}</p>
          )}
        </div>

        <div>
          <Label htmlFor="candidateLinkedin" className="text-slate-700 font-medium">
            LinkedIn Profile
            <span className="text-sm font-normal text-slate-500 ml-2">(Required if no CV)</span>
          </Label>
          <Input
            id="candidateLinkedin"
            type="text"
            inputMode="url"
            {...register("candidateLinkedin")}
            placeholder="https://linkedin.com/in/their-name"
            className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            data-lpignore="true"
            data-form-type="other"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="candidatePhone" className="text-slate-700 font-medium">
            Phone Number
            <span className="text-sm font-normal text-slate-500 ml-2">(Optional)</span>
          </Label>
          <Input
            id="candidatePhone"
            type="tel"
            inputMode="tel"
            {...register("candidatePhone")}
            placeholder="+1 (555) 123-4567"
            autoComplete="tel"
            className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
          />
        </div>
        <div></div>
      </div>

      {/* Either/Or Requirement Notice */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
        <div className="flex items-start gap-3">
          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <svg className="w-3 h-3 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-blue-900">Profile Information Required</h4>
            <p className="text-sm text-blue-700 mt-1">
              Provide either a LinkedIn profile <strong>OR</strong> upload a CV below. Both aren't required, but uploading a CV earns you bonus AiR Points!
            </p>
          </div>
        </div>
      </div>

      {/* CV Upload Section */}
      <div className="space-y-6 pb-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <h3 className="text-xl font-bold text-slate-800">CV</h3>
            <div className="bg-gradient-to-r from-orange-400 to-orange-600 text-white text-xs font-bold px-3 py-1 rounded-full space-grotesk uppercase tracking-wide">
              Bonus
            </div>
          </div>
          <p className="text-slate-600">
            Upload their CV to earn bonus <span className="font-semibold space-grotesk text-orange-600">AiR Points</span> for successful placements
          </p>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <svg className="w-3 h-3 text-slate-400" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span>CV upload = Higher placement success rate + Extra rewards for you!</span>
          </div>
        </div>
        
        <div className="relative">
          {resumeFile || resumeUrl ? (
            <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-gray-600" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900">
                      {resumeFileName || resumeFile?.name || "CV file"}
                    </h4>
                    <p className="text-sm text-gray-500">{resumeUrl ? "CV uploaded successfully" : "Ready to upload with referral"}</p>
                  </div>
                </div>
                <button
                  onClick={handleRemoveResume}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  type="button"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          ) : (
            <div 
              className="w-full bg-white border-2 border-dashed border-gray-300 rounded-lg text-center hover:border-gray-400 transition-colors py-8"
            >
              <div className="flex flex-col items-center">
                <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center mb-3">
                  <Upload className="w-5 h-5 text-gray-400" />
                </div>
                <h4 className="text-base font-medium text-gray-900 mb-1">
                  Choose CV file
                  <span className="text-sm font-normal text-slate-500 ml-2">(Optional but recommended)</span>
                </h4>
                <p className="text-sm text-gray-500 mb-3">
                  PDF, DOC, or DOCX up to 10MB
                </p>
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        // Check file size (10MB limit)
                        if (file.size > 10485760) {
                          // TODO: Integrate with Sentry for error tracking and reporting
                          toast({
                            title: "File too large",
                            description: "Please select a file smaller than 10MB.",
                            variant: "destructive"
                          });
                          e.target.value = ''; // Reset input
                          return;
                        }
                        handleFileSelect(file);
                      }
                    }}
                    className="sr-only"
                    id="resume-upload"
                    disabled={isUploading}
                  />
                  <label htmlFor="resume-upload" className="cursor-pointer">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={isUploading}
                      asChild
                      className="inline-flex items-center px-3 py-2 text-sm font-medium disabled:opacity-50"
                    >
                      <span className="cursor-pointer">
                        {isUploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin mr-2" style={{ animationDuration: '1.5s' }}></div>
                            Uploading CV...
                          </>
                        ) : (
                          "Browse files"
                        )}
                      </span>
                    </Button>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Additional Information Section */}
      <div className="space-y-6">
        <div className="space-y-2">
          <h3 className="text-xl font-bold text-slate-800">Why are you referring them?</h3>
        </div>
        
        <div className="space-y-6">
          <div>
            <Label htmlFor="referralNote" className="text-slate-700 font-medium">Help us understand why your friend would be a great match.</Label>
            <Textarea
              id="referralNote"
              {...register("referralNote")}
              placeholder="Tell us about their skills, experience, and why they'd be perfect for this job. What makes them awesome?"
              rows={4}
              className="mt-2 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200 resize-none"
              autoCapitalize="sentences"
            />
          </div>

          <div>
            <Label htmlFor="relationship" className="text-slate-700 font-medium">How do you know them?</Label>
            <Select 
              value={watchedRelationship} 
              onOpenChange={(open) => {
                const scrollContainer = getScrollContainer();
                
                if (open) {
                  // Lock scroll when opening
                  isSelectingRef.current = true;
                  if (scrollContainer) {
                    scrollPositionRef.current = scrollContainer.scrollTop;
                  }
                } else {
                  // Don't unlock immediately - let onCloseAutoFocus handle it
                  // This ensures the Select has fully closed before unlocking
                }
              }}
              onValueChange={(value) => {
                // Update value - scroll lock will handle position preservation
                setValue("relationship", value, { shouldValidate: false });
              }}
            >
              <SelectTrigger 
                id="relationship" 
                className="mt-2 h-12 bg-white/60 backdrop-blur-sm border-slate-200 focus:border-orange-300 focus:ring-orange-200"
                style={{ scrollMargin: 0 }}
              >
                <SelectValue placeholder="How do you know them..." />
              </SelectTrigger>
              <SelectContent
                className="z-[200]"
                position="popper"
                sideOffset={5}
                onCloseAutoFocus={(e) => {
                  e.preventDefault();
                  const scrollContainer = getScrollContainer();
                  
                  // Restore scroll position if we have one saved
                  if (scrollContainer && scrollPositionRef.current !== null) {
                    scrollContainer.scrollTop = scrollPositionRef.current;
                  }
                  
                  // Unlock scroll after Select has fully closed
                  isSelectingRef.current = false;
                }}
              >
                <SelectItem value="former_colleague">
                  We used to work together
                </SelectItem>
                <SelectItem value="current_colleague">
                  We work together now
                </SelectItem>
                <SelectItem value="friend">
                  They're my friend/mate
                </SelectItem>
                <SelectItem value="professional_acquaintance">
                  We know each other professionally
                </SelectItem>
                <SelectItem value="other">
                  Other
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* GDPR Consent Section */}
      <div className="space-y-4 pt-6 border-t border-slate-200">
        <div className="bg-gradient-to-r from-orange-50/50 to-orange-100/30 border border-orange-200/40 rounded-xl p-5 backdrop-blur-sm">
          <div className="flex items-start gap-4">
            <div className="pt-1 flex-shrink-0">
              <Checkbox
                id="gdprConsent"
                checked={gdprConsent}
                onCheckedChange={(checked) => handleGdprChange(!!checked)}
                className="h-5 w-5 border-2 border-orange-300 data-[state=checked]:bg-gradient-to-b data-[state=checked]:from-orange-400 data-[state=checked]:to-orange-600 data-[state=checked]:border-orange-500 focus-visible:ring-2 focus-visible:ring-orange-200 focus-visible:ring-offset-2 transition-all duration-200 shadow-sm hover:border-orange-400 hover:shadow-md data-[state=checked]:text-white"
                style={{ 
                  scrollMargin: 0,
                  scrollSnapAlign: 'none'
                }}
                required
              />
            </div>
            <label htmlFor="gdprConsent" className="text-sm text-slate-700 leading-relaxed cursor-pointer flex-1">
              <span className="font-semibold text-slate-800 bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent space-grotesk">Permission Confirmation:</span> I confirm that I have their permission to submit their personal information (including CV and contact details) to this platform for job opportunities. They understand their data will be processed according to our privacy policy and they can withdraw consent anytime.
            </label>
          </div>
          
          {/* Subtle decorative element */}
          <div className="flex items-center gap-2 mt-3 text-xs text-orange-600/70">
            <div className="w-1.5 h-1.5 bg-orange-400 rounded-full"></div>
            <span className="font-medium space-grotesk">Required for referral submission</span>
          </div>
        </div>
      </div>
    </>
  ), [register, errors, resumeFile, resumeUrl, resumeFileName, isUploading, watchedRelationship, gdprConsent, handleFileSelect, handleRemoveResume, handleGdprChange, getScrollContainer]);

  return (
    <>
      {/* Success Message Overlay */}
      {showSuccess && <SuccessMessage />}
      
      {/* Duplicate Referral Alert Dialog */}
      <AlertDialog open={duplicateAlert.show} onOpenChange={(open) => !open && setDuplicateAlert({ show: false, title: "", description: "" })}>
        <AlertDialogContent className="bg-white border-red-200 shadow-2xl max-w-md">
          <AlertDialogHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
            </div>
            <AlertDialogTitle className="text-xl font-bold text-slate-800">
              {duplicateAlert.title}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600 mt-2 text-base">
              {duplicateAlert.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex justify-center mt-6">
            <AlertDialogAction 
              onClick={() => setDuplicateAlert({ show: false, title: "", description: "" })}
              asChild
            >
              <Button variant="primary" size="md">
                Got it!
              </Button>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      
      {/* Single Unified Form - Responsive */}
      <div className="w-full lg:w-3/5">
        <form id={formId} onSubmit={handleSubmit(onSubmit)} className="p-6 lg:p-8">
          <div className="space-y-8 pb-24">
            {FormContent}
            
            {/* Action Buttons */}
            <div className="border-t border-slate-200 bg-gradient-to-r from-white/80 to-slate-50/80 backdrop-blur-sm p-6 mt-8 rounded-lg">
              <div className="flex flex-col lg:flex-row justify-end space-y-3 lg:space-y-0 lg:space-x-4">
                <Button 
                  type="button" 
                  variant="secondary"
                  size="md"
                  onClick={onClose}
                  className="w-full lg:w-auto"
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  variant="primary"
                  size="md"
                  disabled={mutation.isPending || isUploading || !(watchedLinkedin || resumeFile || resumeUrl) || !gdprConsent}
                  className="w-full lg:w-auto uppercase"
                >
                  {mutation.isPending || isUploading ? (
                    <div className="flex items-center space-x-2">
                      {isUploading ? (
                        <>
                          <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20" style={{ animation: 'bounce 2s infinite' }}>
                            <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span>Uploading CV...</span>
                        </>
                      ) : (
                        <>
                          <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" style={{ animationDuration: '1.2s' }}></div>
                          <span>Creating Referral...</span>
                        </>
                      )}
                    </div>
                  ) : (
                    "Submit Referral"
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </>
  );
}